import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Snackbar,
  Radio,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  GlobalStyles,
  useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme, keyframes } from '@mui/material/styles';
import { styled } from '@mui/system';
import { alpha } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../utils/supabaseClient';
import { getCompanyNameFromDOT, getCompanyNameFromMC } from '../api/auth';
import SaveIcon from '@mui/icons-material/Save';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import mapBg from '../assests/map.jpg';
import SD_logo from '../assests/shipperdock.png';
import TermsAndConditions from '../components/TermsAndConditions';
import PrivacyPolicy from '../components/PrivacyPolicy';
import { auth } from '../utils/firebaseClient';

// ─── Theme (same as AuthPage) ────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary: { main: '#11224d' },
    secondary: { main: '#0e1d3a' },
    info: { main: '#4dabf5' },
    action: {
      disabledBackground: 'rgba(17, 34, 77, 0.5)',
      disabled: '#ffffff',
    },
  },
  typography: { fontFamily: 'Inter, Arial, sans-serif' },
  components: {
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none' } } },
    MuiPaper: { styleOverrides: { root: { borderRadius: 16 } } },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          transform: 'translate(14px, -8px) scale(0.75) !important',
          backgroundColor: '#ffffff',
          padding: '0 4px',
          zIndex: 1,
        },
      },
    },
  },
});

const brandColors = {
  primary: '#11224d',
  textLight: '#5c6b85',
};

// ─── Animations ──────────────────────────────────────────────────────────────
const truckDrive = keyframes`
  0%   { transform: translateY(-10px); }
  50%  { transform: translateY(10px);  }
  100% { transform: translateY(-10px); }
`;

// ─── Styled Components (mirrors AuthPage) ────────────────────────────────────
const TruckWrap = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  '& .animated-truck': {
    animation: `${truckDrive} 2.4s ease-in-out infinite`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const GradientPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  width: '100%',
  maxWidth: '900px',
  boxSizing: 'border-box',
  borderRadius: '16px',

  '& .left-panel': {
    background: 'linear-gradient(180deg, #11224d 0%, #1a3a6d 100%)',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    width: '40%',
    boxSizing: 'border-box',
  },
  '& .right-panel': {
    background: '#f8fafd',
    padding: theme.spacing(4),
    width: '60%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
    boxSizing: 'border-box',
    overflowY: 'auto',
    maxHeight: '100vh',
  },

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    background: '#ffffff',
    backdropFilter: 'none',
    border: 'none',
    margin: theme.spacing(1),
    '& .left-panel': {
      width: '100%',
      padding: theme.spacing(4, 2),
      borderRadius: '16px 16px 0 0',
    },
    '& .right-panel': {
      width: '100%',
      padding: theme.spacing(4, 2),
      background: '#ffffff',
      maxHeight: 'none',
    },
  },
}));

const BackgroundImageLayer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'loaded',
})(({ loaded }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `
    radial-gradient(circle at center, rgba(14, 29, 58, 0.4) 0%, rgba(14, 29, 58, 0.9) 100%),
    url(${mapBg})
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  opacity: loaded ? 1 : 0,
  transition: 'opacity 1.5s ease-in-out',
  zIndex: 0,
}));

const PageContainer = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#0e1d3a',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(77, 171, 245, 0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(77, 171, 245, 0.08) 1px, transparent 1px)
    `,
    backgroundSize: '20px 20px',
    pointerEvents: 'none',
    zIndex: 1,
  },
});

const ContentWrapper = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3, 0),
  position: 'relative',
  zIndex: 2,
  width: '100%',
}));

// ─── Helper constants ─────────────────────────────────────────────────────────
const CLEARED_COMPANY_FIELDS = {
  company_name: '',
  dba_name: '',
  street: '',
  city_st_zip: '',
  power_units: '',
  mc_number: '',
};

const validateMCNumber = (mcNumber) => {
  const digits = mcNumber.replace(/^MC-/i, '').replace(/\D/g, '');
  if (!digits) return 'MC number is required';
  if (digits.length < 6) return 'MC number must be at least 6 digits';
  if (digits.length > 8) return 'MC number must be no more than 8 digits';
  return '';
};

const validateDOTNumber = (dotNumber) => {
  const cleaned = dotNumber.replace(/\D/g, '');
  if (!cleaned) return 'DOT number is required';
  if (cleaned.length < 6) return 'DOT number must be at least 6 digits';
  if (cleaned.length > 8) return 'DOT number must be no more than 8 digits';
  return '';
};

// ─── Inner Component ──────────────────────────────────────────────────────────
function SetupProfileInner() {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role');

  const [dotDialog, setDotDialog] = useState({ open: false, status: 'idle' });

  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isAutofilled, setIsAutofilled] = useState(false);
  const [errors, setErrors] = useState({});
  const [bgLoaded, setBgLoaded] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [planPrice, setPlanPrice] = useState(null);
  const [trialDays, setTrialDays] = useState(null);
  const [isPricingLoading, setIsPricingLoading] = useState(true);

  const [formState, setFormState] = useState({
    company_name: '',
    dba_name: '',
    street: '',
    city_st_zip: '',
    phone_number: '',
    employees: '',
    dot_number: '',
    mc_number: '',
    fmcsa_email: '',
    power_units: '',
  });

  // Load background image
  useEffect(() => {
    const img = new Image();
    img.src = mapBg;
    img.onload = () => setBgLoaded(true);
  }, []);

  // Auth check
  useEffect(() => {
  // onAuthStateChanged is reliable — currentUser alone can be null on first render
  const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
    if (!firebaseUser) {
      navigate('/');
      return;
    }
    // Match the shape your payload uses: currentUser.id and currentUser.email
    setCurrentUser({
      id: firebaseUser.uid,
      email: firebaseUser.email,
    });
  });
  return () => unsubscribe(); // cleanup on unmount
}, [navigate]);

  // Fetch pricing (same as AuthForm)
  useEffect(() => {
    const fetchPricingDetails = async () => {
      setIsPricingLoading(true);
      setPlanPrice(null);
      try {
        const priceIdMap = {
          shipper: process.env.REACT_APP_STRIPE_PRICE_ID_SHIPPER,
          carrier: process.env.REACT_APP_STRIPE_PRICE_ID_CARRIER,
          broker: process.env.REACT_APP_STRIPE_PRICE_ID_BROKER,
        };
        const selectedPriceId = priceIdMap[role];
        if (!selectedPriceId) { setIsPricingLoading(false); return; }

        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        const token = localStorage.getItem('jwtToken');

        const response = await axios.post(
          `${API_BASE}/api/stripe/create-subscription`,
          { priceId: selectedPriceId, previewOnly: true },
          { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
        );

        if (response.data.amount !== undefined) setPlanPrice(response.data.amount);
        if (response.data.trialDays !== undefined) setTrialDays(response.data.trialDays);
      } catch (err) {
        console.error('Pricing fetch error:', err);
      } finally {
        setIsPricingLoading(false);
      }
    };
    if (role) fetchPricingDetails();
  }, [role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhoneNumberChange = (e) => {
    const rawDigits = e.target.value.replace(/[^0-9]/g, '');
    if (rawDigits.length > 10) return;

    let displayValue = rawDigits;
    if (rawDigits.length > 6) {
      displayValue = `(${rawDigits.slice(0, 3)}) ${rawDigits.slice(3, 6)}-${rawDigits.slice(6, 10)}`;
    } else if (rawDigits.length > 3) {
      displayValue = `(${rawDigits.slice(0, 3)}) ${rawDigits.slice(3)}`;
    }
    setFormState(prev => ({ ...prev, phone_number: displayValue }));
    setErrors(prev => ({ ...prev, phone_number: '' }));
  };

  // Auto-fetch company info from DOT/MC — same logic as old AuthForm
  const handleDotChange = async (e) => {
    const { value } = e.target;
    const displayValue = value.replace(/\D/g, '');

    setFormState(prev => ({
      ...prev,
      dot_number: displayValue,
      ...CLEARED_COMPANY_FIELDS,
    }));
    setIsAutofilled(false);
    setErrors(prev => ({ ...prev, dot_number: '' }));

    const validationError = validateDOTNumber(displayValue);

    if (validationError) {
      setErrors(prev => ({ ...prev, dot_number: validationError }));
      return;
    }

    if (displayValue.length >= 6) {
      // Open dialog in loading state
      setDotDialog({ open: true, status: 'loading' });
      try {
        const data = await getCompanyNameFromDOT(displayValue);

        if (data) {
          setFormState(prev => ({
            ...prev,
            company_name: data.legalName || prev.company_name,
            dba_name: data.dbaName || prev.dba_name,
            street: data.street || prev.street,
            city_st_zip: data.cityStZip || prev.city_st_zip,
            power_units: data.powerUnits || prev.power_units,
            mc_number: (data.mcNumber || data.mc_number) ? `MC-${(data.mcNumber || data.mc_number).toString().replace(/^MC-?/i, '')}` : '',
          }));
          setIsAutofilled(true);
          // Close dialog on success
          setDotDialog({ open: false, status: 'idle' });
        }
      } catch (error) {
        console.error('Lookup error:', error);
        // Switch dialog to error state
        setDotDialog({ open: true, status: 'error' });
      }
    }
  };

  const handleMcChange = (e) => {
    let val = e.target.value.toUpperCase();
    if (!val.startsWith('MC-')) val = 'MC-' + val.replace(/^MC-?/, '');
    const digits = val.split('MC-')[1] || '';
    setFormState(prev => ({ ...prev, mc_number: `MC-${digits.replace(/\D/g, '')}` }));
  };

  const validateForm = () => {
  const newErrors = {};
  
  const validateCompanyName = (name) => {
    if (!name || name.trim().length < 2) return 'Company name must be at least 2 characters';
    if (name.length > 200) return 'Company name is too long';
    return '';
  };

  const validateCityStateZip = (value) => {
    if (!value || value.trim().length < 5) return 'City, State, Zip is required';
    return '';
  };

  const phoneCleaned = formState.phone_number.replace(/\D/g, '');
  if (phoneCleaned.length !== 10) {
    newErrors.phone_number = 'Phone number must be exactly 10 digits';
  } else if (phoneCleaned[0] === '0' || phoneCleaned[0] === '1') {
    newErrors.phone_number = 'Area code cannot start with 0 or 1';
  }

  const companyError = validateCompanyName(formState.company_name);
  if (companyError) newErrors.company_name = companyError;

  const cityError = validateCityStateZip(formState.city_st_zip);
  if (cityError) newErrors.city_st_zip = cityError;

  if (!formState.company_name) newErrors.company_name = 'Company Name is required';
  if (!formState.street) newErrors.street = 'Street Address is required';
  if (!formState.city_st_zip) newErrors.city_st_zip = 'City, State, Zip is required';
  if (!formState.phone_number) newErrors.phone_number = 'Phone Number is required';
  if (!formState.fmcsa_email) 
  newErrors.fmcsa_email = 'Business Email is required';

  if (role === 'carrier' || role === 'broker') {
    const idError = validateDOTNumber(formState.dot_number);
    if (formState.mc_number && formState.mc_number !== 'MC-' && formState.mc_number !== '' && !idError) {
        const mcErr = validateMCNumber(formState.mc_number);
        if (mcErr) newErrors.mc_number = mcErr;
    }
    if (idError) newErrors.dot_number = idError;
  }
  

  if (role === 'carrier') {
    const units = Number(formState.power_units);
    if (!formState.power_units && formState.power_units !== 0)
      newErrors.power_units = 'Power Units is required';
    else if (!Number.isInteger(units) || units < 1)
      newErrors.power_units = 'Must be a whole number of at least 1';
    else if (units > 99999)
      newErrors.power_units = 'Value seems too large — please double-check';
  }

  if (role === 'shipper' && !formState.employees)
    newErrors.employees = 'Number of Employees is required';

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const firebaseUser = auth.currentUser;
if (!firebaseUser) throw new Error('Not authenticated. Please sign in again.');
const token = await firebaseUser.getIdToken();
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

      const payload = {
        google_uid: currentUser.id,
        email: currentUser.email,
        company_name: formState.company_name,
        dba_name: formState.dba_name,
        street: formState.street,
        city_st_zip: formState.city_st_zip,
        phone_number: formState.phone_number,
        role,
      };
      
      payload.fmcsa_email = formState.fmcsa_email;
      if (role === 'carrier' || role === 'broker') {
        payload.dot_number = formState.dot_number;
        payload.mc_number = formState.mc_number;
        payload.fmcsa_email = formState.fmcsa_email;
      }
      if (role === 'carrier') payload.power_units = formState.power_units;
      if (role === 'shipper') payload.employees = formState.employees;

      const response = await axios.post(
        `${API_BASE_URL}/api/auth/complete-google-profile/${role}`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      localStorage.setItem('jwtToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('role', role);
      sessionStorage.setItem('session_active', 'true');

      setSnackbar({ open: true, message: 'Profile setup complete!', severity: 'success' });

      setTimeout(() => {
  if (role === 'carrier') {
    // Carriers go directly to payment
    navigate('/payment', { replace: true });
  } else {
    // Shippers & Brokers go to their dashboard
    navigate(`/dashboard/${role}`, { replace: true });
  }
}, 1500);
    } catch (err) {
      console.error('Profile setup error:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to setup profile',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser || !role) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0e1d3a' }}>
        <CircularProgress sx={{ color: '#4dabf5' }} />
      </Box>
    );
  }

  // ─── ID Type field block ───────────────────────────────────────────────────
  const renderIdTypeFields = () => (
    <>
      <TextField
        fullWidth
        label="DOT Number"
        name="dot_number"
        value={formState.dot_number}
        onChange={handleDotChange}
        error={!!errors.dot_number}
        helperText={errors.dot_number || errors.dot_number_hint || 'Enter your DOT to fetch your details.'}
        required
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        fullWidth
        label="MC Number (If Any)"
        name="mc_number"
        value={formState.mc_number}
        onChange={handleMcChange}
        helperText=""
        disabled={true}
        InputLabelProps={{ shrink: true }}
      />
    </>
  );

  // ─── Pricing banner (same as AuthForm) ────────────────────────────────────
  const renderPricingBanner = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1,
        backgroundColor: '#f8fafd',
        mt: 1,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <Box
        sx={{
          width: 36, height: 36, borderRadius: '50%',
          backgroundColor:
            role === 'shipper' ? '#e3f2fd'
              : role === 'carrier' ? '#fff8e1'
                : '#f3e5f5',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        {role === 'shipper' && <BusinessIcon sx={{ fontSize: 18, color: '#1565c0' }} />}
        {role === 'carrier' && <LocalShippingIcon sx={{ fontSize: 18, color: '#f9a825' }} />}
        {role === 'broker' && <GroupsIcon sx={{ fontSize: 18, color: '#6a1b9a' }} />}
      </Box>

      <Box sx={{ flex: 1, minWidth: { xs: '150px', sm: 'auto' } }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
          Your plan after trial
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#11224d', lineHeight: 1.4, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {isPricingLoading
            ? 'Updating trial period...'
            : role === 'carrier'
              ? `${trialDays || 90}-day free trial included`
              : <>Free access<br />No subscription required</>}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
          {role === 'carrier'
            ? (!isPricingLoading && planPrice !== null
              ? `$${Number(planPrice).toFixed(2)}/month after trial · Carrier plan`
              : isPricingLoading ? 'Fetching price details...' : 'Pricing currently unavailable')
            : `${role ? role.charAt(0).toUpperCase() + role.slice(1) : ''} plan · No billing required`}
        </Typography>
      </Box>

      <Box
        sx={{
          fontSize: '11px', fontWeight: 600, px: 1, py: 0.4,
          borderRadius: '99px',
          backgroundColor: role === 'carrier' ? '#e8f5e9' : '#e3f2fd',
          color: role === 'carrier' ? '#2e7d32' : '#1565c0',
          whiteSpace: 'nowrap',
        }}
      >
        {role === 'carrier' ? 'Free trial' : 'Free access'}
      </Box>
    </Box>
  );

  return (
    <PageContainer>
      <GlobalStyles
        styles={{
          '.right-panel .MuiInputBase-root': { backgroundColor: '#ffffff' },
          '.right-panel input:-webkit-autofill, .right-panel input:-webkit-autofill:hover, .right-panel input:-webkit-autofill:focus':
          {
            WebkitBoxShadow: '0 0 0px 1000px #ffffff inset !important',
            WebkitTextFillColor: '#11224d !important',
            caretColor: '#11224d',
          },
        }}
      />

      <BackgroundImageLayer loaded={bgLoaded} />

      <ContentWrapper>
        <Container
          component="main"
          maxWidth="md"
          disableGutters={isMobile}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <GradientPaper elevation={6}>
            {/* ── Left Panel ── */}
            <Box className="left-panel">
              {isMobile ? (
                <Box
                  component="img"
                  src={SD_logo}
                  alt="ShipperDock"
                  sx={{ width: '200px', height: 'auto', display: 'block', mx: 'auto' }}
                />
              ) : (
                <TruckWrap>
                  <div className="animated-truck">
                    <LocalShippingIcon style={{ fontSize: '150px', color: 'white' }} />
                  </div>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>ShipperDock</Typography>
                </TruckWrap>
              )}
            </Box>

            {/* ── Right Panel ── */}
            <Paper elevation={isMobile ? 0 : 3} className="right-panel">
              <Typography variant="h5" align="center" sx={{ mb: 0.5, fontWeight: 700, color: brandColors.primary }}>
                Complete Your Profile
              </Typography>
              <Typography variant="body2" align="center" sx={{ mb: 2, color: brandColors.textLight }}>
                Signed in as: {currentUser.email}
              </Typography>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                {/* DOT / MC — right after FMCSA for carrier & broker */}
                {(role === 'carrier' || role === 'broker') && renderIdTypeFields()}

                {/* FMCSA Email */}
               
                <TextField
  fullWidth
  label="Business Email"
  name="fmcsa_email"
  type="email"
  value={formState.fmcsa_email}
  onChange={handleChange}
  error={!!errors.fmcsa_email}
  helperText={errors.fmcsa_email}
  required
/>

                {/* Company Name */}
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formState.company_name}
                  onChange={handleChange}
                  error={!!errors.company_name}
                  helperText={errors.company_name}
                  disabled={role === 'carrier' || role === 'broker'}
                  required
                  InputLabelProps={{ shrink: !!formState.company_name }}
                />

                {/* DBA Name */}
                <TextField
                  fullWidth
                  label="DBA Name (Optional)"
                  name="dba_name"
                  value={formState.dba_name}
                  onChange={handleChange}
                  disabled={role === 'carrier' || role === 'broker'}
                  InputLabelProps={{ shrink: !!formState.dba_name }}
                />

                {/* Street */}
                <TextField
                  fullWidth
                  label="Street Address"
                  name="street"
                  value={formState.street}
                  onChange={handleChange}
                  error={!!errors.street}
                  helperText={errors.street}
                  disabled={role === 'carrier' || role === 'broker'}
                  required
                  InputLabelProps={{ shrink: !!formState.street }}
                />

                {/* City / State / Zip */}
                <TextField
                  fullWidth
                  label="City, State, Zip"
                  name="city_st_zip"
                  value={formState.city_st_zip}
                  onChange={handleChange}
                  error={!!errors.city_st_zip}
                  helperText={errors.city_st_zip || 'e.g., Los Angeles, CA 90001'}
                  disabled={role === 'carrier' || role === 'broker'}
                  required
                  InputLabelProps={{ shrink: !!formState.city_st_zip }}
                />

                {/* Phone */}
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  type="tel"
                  value={formState.phone_number}
                  onChange={handlePhoneNumberChange}
                  error={!!errors.phone_number}
                  helperText={errors.phone_number || '(XXX) XXX-XXXX'}
                  required
                  placeholder="(555) 123-4567"
                />

                {/* Carrier: Power Units */}
                {role === 'carrier' && (
                  <TextField
                    fullWidth
                    label="Number of Power Units"
                    name="power_units"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    disabled={true}
                    value={formState.power_units}
                    onChange={handleChange}
                    error={!!errors.power_units}
                    helperText={errors.power_units}
                    required  
                    InputLabelProps={{ shrink: !!formState.power_units }}
                  />
                )}

                {/* Broker: disabled power units = 0 */}
                {role === 'broker' && (
                  <TextField
                    disabled
                    fullWidth
                    label="Number of Power Units"
                    name="power_units"
                    type="number"
                    value={0}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: '8px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d1d1 !important' },
                      '& .MuiInputLabel-root.Mui-disabled': { color: '#757575' },
                      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#9e9e9e' },
                    }}
                  />
                )}

                {/* Shipper: Employees */}
                {role === 'shipper' && (
                  <TextField
                    fullWidth
                    label="Number of Employees"
                    name="employees"
                    type="number"
                    value={formState.employees}
                    onChange={handleChange}
                    error={!!errors.employees}
                    helperText={errors.employees}
                    required
                  />
                )}

                {/* Terms & Privacy */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreementChecked}
                      onChange={(e) => setAgreementChecked(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      I agree to the{' '}
                      <span
                        style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                      >
                        Terms of Service
                      </span>{' '}
                      and{' '}
                      <span
                        style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}
                      >
                        Privacy Policy
                      </span>
                    </Typography>
                  }
                />

                {/* Submit */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !agreementChecked}
                  startIcon={!loading && <SaveIcon />}
                  sx={{
                    py: 1.5,
                    bgcolor: '#11224d',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#0e1d3a' },
                    '&.Mui-disabled': { bgcolor: 'rgba(17,34,77,0.5)', color: '#fff' },
                  }}
                >
                  {loading
                    ? <CircularProgress size={24} sx={{ color: 'white' }} />
                    : 'Continue to Next Step'}
                </Button>

                {/* Pricing Banner */}
                {renderPricingBanner()}
              </Box>
            </Paper>
          </GradientPaper>
        </Container>
      </ContentWrapper>

      {/* Terms Dialog */}
      <Dialog open={showTerms} onClose={() => setShowTerms(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <TermsAndConditions
            isDialog
            onNavigate={(page) => {
              if (page === 'Privacy Policy') { setShowTerms(false); setShowPrivacy(true); }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTerms(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Dialog */}
      <Dialog open={showPrivacy} onClose={() => setShowPrivacy(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <PrivacyPolicy isDialog />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrivacy(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={dotDialog.open} 
        onClose={() => dotDialog.status === 'error' && setDotDialog({ open: false, status: 'idle' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, textAlign: 'center', color: dotDialog.status === 'error' ? 'error.main' : 'inherit' }}>
          {dotDialog.status === 'loading' ? 'Fetching Details' : 'Lookup Failed'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 1 }}>
          {dotDialog.status === 'loading' ? (
            <>
              <CircularProgress size={32} />
              <Typography sx={{ textAlign: 'center', fontSize: '0.95rem' }}>
                Fetching your company details from FMCSA…
              </Typography>
            </>
          ) : (
            <Typography sx={{ textAlign: 'center', fontSize: '0.95rem' }}>
              FMCSA lookup unavailable from your current location
            </Typography>
          )}
        </DialogContent>
        {dotDialog.status === 'error' && (
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
            <Button 
              onClick={() => setDotDialog({ open: false, status: 'idle' })} 
              variant="contained"
              sx={{ bgcolor: '#11224d' }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}

export default function SetupProfilePage() {
  return (
    <ThemeProvider theme={theme}>
      <SetupProfileInner />
    </ThemeProvider>
  );
}